/**
 * AllSpecialties.jsx - Dedicated specialties listing page
 * 
 * Displays all medical specialties with:
 * - Full descriptions
 * - Treatment lists
 * - Related doctors count
 * - CTA to enquiry form
 * 
 * SEO-optimized with proper canonical URL
 * 
 * @author Curis Path Development Team
 */

import React from 'react';
import { specialties, doctors } from '../data/mock';
import { ArrowRight, Users, Stethoscope } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';

const AllSpecialties = () => {
  const navigate = useNavigate();

  // Count doctors per specialty
  const getDoctorCount = (specialtyName) => {
    const specialtyMap = {
      'Orthopedic Surgery': 'Orthopedic Surgeon',
      'Oncology': 'Oncologist',
      'Dental Care': 'Dental Specialist',
      'Cosmetic Surgery': 'Cosmetic Surgeon'
    };
    const doctorSpecialty = specialtyMap[specialtyName];
    return doctors.filter(d => d.specialty === doctorSpecialty).length;
  };

  // Scroll to enquiry form
  const scrollToEnquiry = () => {
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById('enquiry');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  return (
    <div>
      {/* SEO Meta Tags with self-referencing canonical */}
      <SEOHead 
        title="Medical Specialties | Orthopedics, Oncology, Dental, Cosmetic Surgery - Curis Path"
        description="Explore our 4 medical specialties: Orthopedic Surgery, Oncology, Dental Care, and Cosmetic Surgery. Save up to 70% on procedures at India's top hospitals."
        canonical="/specialties"
      />
      
      <Header />
      
      <main style={{ background: 'var(--bg-page)', minHeight: '100vh' }}>
        <div className="container py-12">
          {/* Page Header */}
          <div className="text-center mb-12">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
              style={{ background: 'var(--bg-section)' }}
            >
              <Stethoscope size={16} style={{ color: 'var(--text-primary)' }} />
              <span className="caption" style={{ color: 'var(--text-secondary)' }}>
                World-Class Medical Care
              </span>
            </div>
            <h1 className="heading-1 mb-4">Our Medical Specialties</h1>
            <p 
              className="body-large mx-auto" 
              style={{ maxWidth: '700px', color: 'var(--text-secondary)' }}
            >
              Comprehensive healthcare solutions across 4 major specialties with 
              internationally trained doctors and state-of-the-art facilities
            </p>
          </div>

          {/* Specialties Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {specialties.map((specialty) => {
              const doctorCount = getDoctorCount(specialty.name);
              
              return (
                <div 
                  key={specialty.id} 
                  className={`voice-card ${specialty.colorClass}`}
                >
                  {/* Specialty Image */}
                  <div 
                    className="mb-4 rounded-lg overflow-hidden" 
                    style={{ height: '220px' }}
                  >
                    <img 
                      src={specialty.image} 
                      alt={`${specialty.name} treatment in India`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  
                  {/* Specialty Name */}
                  <h2 className="heading-2 mb-3">{specialty.name}</h2>
                  
                  {/* Doctor Count Badge */}
                  <div 
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4"
                    style={{ background: 'rgba(255, 255, 255, 0.6)' }}
                  >
                    <Users size={14} style={{ color: 'var(--text-primary)' }} />
                    <span className="caption font-semibold" style={{ color: 'var(--text-primary)' }}>
                      {doctorCount} Expert Doctors
                    </span>
                  </div>
                  
                  {/* Description */}
                  <p 
                    className="body-small mb-4" 
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {specialty.description}
                  </p>

                  {/* Key Treatments */}
                  <div className="mb-6">
                    <div 
                      className="caption font-semibold mb-2" 
                      style={{ color: 'var(--text-primary)' }}
                    >
                      Key Treatments:
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {specialty.treatments.map((treatment, index) => (
                        <span 
                          key={index}
                          className="caption px-3 py-1.5 rounded-full"
                          style={{ 
                            background: 'rgba(255, 255, 255, 0.5)',
                            border: '1px solid rgba(0, 0, 0, 0.1)',
                            color: 'var(--text-secondary)'
                          }}
                        >
                          {treatment}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex gap-3">
                    <button 
                      className="btn-primary flex-1 flex items-center justify-center gap-2"
                      onClick={scrollToEnquiry}
                    >
                      Get Quote
                      <ArrowRight size={16} />
                    </button>
                    <button 
                      className="btn-secondary"
                      onClick={() => navigate('/doctors')}
                    >
                      View Doctors
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-12">
            <p 
              className="body-large mb-4" 
              style={{ color: 'var(--text-secondary)' }}
            >
              Not sure which specialty you need?
            </p>
            <button 
              className="btn-primary inline-flex items-center gap-2"
              onClick={scrollToEnquiry}
            >
              Contact Us for Free Consultation
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AllSpecialties;
